import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Columns3, X } from 'lucide-react';
import type { Asset, AssetCard } from '@/lib/api/types';
import type { BoardCardEntry } from '@/features/grid/types';
import { isPlaceholder } from '@/features/grid/types';
import { api, req } from '@/lib/api/client';
import { keys } from '@/lib/api/keys';
import { patchCardEverywhere, useBulkAction, useBulkSetAssetRights, useFields, useSetApproval, useTags, useUpdateAsset, type BulkRightsResult } from '@/lib/api/queries';
import { useSessionStore } from '@/stores/session';
import { useSelection } from '@/stores/selection';
import { toast } from '@/stores/ui';
import { isApiError } from '@/lib/api/errors';
import { hasPermission } from '@/lib/shared';
import { useEffectiveMask } from '@/features/workspace/usePermission';
import { APPROVAL_TONE, Badge } from '@/components/Misc';
import { Input, Select } from '@/components/Input';
import { cx, formatBytes, formatDateTime, plural } from '@/lib/format';
import { msToClock } from '@/lib/media/timecode';
import { useScrollContainer } from '@/features/grid/ScrollContainer';
import { RIGHTS_STATUS_LABEL, RIGHTS_STATUS_TONE } from '@/features/rights/status';
import { APPROVAL_STATUSES, bulkPlan, cellText, isEditable, parseCell, sourceValue, tagsOf, type CellValue, type ListColumn } from './assetListCells';
import { useRowDetail } from './rowDetail';

const FIELD_COLUMNS_KEY = 'lumen.list.fieldColumns';
const DEFAULT_FIELD_COLUMNS = 3;

/**
 * List view (`?view=list`), editable in place (Release 2 G5). Arrow keys move the active cell, Enter / F2 edits it
 * (Enter again saves and moves down, Escape cancels), Space or Shift+↑/↓ select rows, Ctrl+D fills the active cell's
 * value into the other selected rows and a paste writes into the selected rows (one line per row for multi-line
 * text) — both as one bulk request per distinct value. Cells the caller cannot edit stay read-only (asset
 * `effective_mask` when cached, else the board's). Not virtualised beyond the loaded pages; rows are light, and field
 * cells read the asset detail only for rows that stay in view, through a capped queue (`rowDetail.ts`).
 */
export function AssetListView({ cards, onOpen, hasNextPage, fetchNextPage }: { cards: BoardCardEntry[]; onOpen: (c: AssetCard) => void; hasNextPage?: boolean; fetchNextPage: () => void }) {
  const sentinel = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const root = useScrollContainer();
  useEffect(() => {
    if (!sentinel.current || !hasNextPage) return;
    const io = new IntersectionObserver((es) => es.some((e) => e.isIntersecting) && fetchNextPage(), { root, rootMargin: '800px' });
    io.observe(sentinel.current);
    return () => io.disconnect();
  }, [hasNextPage, fetchNextPage, root]);

  const { boardId } = useParams();
  const wsId = useSessionStore((s) => s.workspace?.id ?? '');
  const qc = useQueryClient();
  const fields = useFields(wsId || undefined);
  const assetFields = useMemo(() => (fields.data ?? []).filter((f) => f.applies_to === 'asset'), [fields.data]);
  const [fieldIds, setFieldIds] = useFieldColumns();
  const shownFields = useMemo(() => (fieldIds ? assetFields.filter((f) => fieldIds.includes(f.id)) : assetFields.slice(0, DEFAULT_FIELD_COLUMNS)), [assetFields, fieldIds]);
  const cols = useMemo<ListColumn[]>(
    () => [
      { key: 'select', kind: 'select', label: 'Select', className: 'w-8' },
      { key: 'thumb', kind: 'thumb', label: 'Preview', className: 'w-12' },
      { key: 'title', kind: 'title', label: 'Name', className: 'min-w-[12rem]' },
      { key: 'type', kind: 'type', label: 'Type', className: 'hidden md:table-cell' },
      { key: 'approval', kind: 'approval', label: 'Approval' },
      { key: 'tags', kind: 'tags', label: 'Tags', className: 'min-w-[10rem]' },
      { key: 'rights', kind: 'rights', label: 'Rights expire' },
      ...shownFields.map((f): ListColumn => ({ key: `field:${f.id}`, kind: 'field', label: f.name, field: f })),
      { key: 'size', kind: 'size', label: 'Size', className: 'hidden md:table-cell' },
      { key: 'added', kind: 'added', label: 'Added', className: 'hidden lg:table-cell' },
    ],
    [shownFields],
  );

  // selection is the grid's store, so the bulk toolbar works on list rows too; `order` indexes the asset rows
  const selected = useSelection((s) => s.ids);
  const order = useMemo(() => cards.filter((c) => !isPlaceholder(c)).map((c) => c.id), [cards]);
  const orderIndex = useMemo(() => new Map(order.map((id, i) => [id, i])), [order]);
  useEffect(() => useSelection.getState().setOrder(order), [order]);

  const boardMask = useEffectiveMask({ boardId });
  const maskOf = useCallback((c: AssetCard) => Number(qc.getQueryData<Asset>(keys.asset(c.id))?.effective_mask ?? boardMask), [qc, boardMask]);
  const canEditCard = useCallback((c: AssetCard) => hasPermission(maskOf(c), 'EDIT_METADATA'), [maskOf]);
  const canApproveCard = useCallback((c: AssetCard) => hasPermission(maskOf(c), 'APPROVE'), [maskOf]);
  const ops = useListOps(wsId);

  const [active, setActive] = useState<{ r: number; c: number }>({ r: 0, c: 2 });
  const [editing, setEditing] = useState(false);
  const [focusTick, setFocusTick] = useState(0);
  useEffect(() => {
    if (focusTick) tableRef.current?.querySelector<HTMLElement>(`[data-cell="${active.r}:${active.c}"]`)?.focus();
  }, [focusTick, active]);
  const moveTo = (r: number, c: number) => {
    setActive({ r: Math.max(0, Math.min(cards.length - 1, r)), c: Math.max(0, Math.min(cols.length - 1, c)) });
    setFocusTick((t) => t + 1);
  };
  const assetCard = (r: number): AssetCard | null => {
    const c = cards[r];
    return c && !isPlaceholder(c) ? c : null;
  };
  const detail = (c: AssetCard) => qc.getQueryData<Asset>(keys.asset(c.id)) ?? null;

  const toggleRow = (c: AssetCard) => useSelection.getState().toggle(c.id, orderIndex.get(c.id) ?? 0);
  const rangeRow = (c: AssetCard) => {
    const sel = useSelection.getState();
    const idx = orderIndex.get(c.id) ?? 0;
    if (sel.anchorIndex == null || sel.ids.size === 0) sel.toggle(c.id, idx);
    else sel.rangeTo(idx);
  };

  /** Commit typed text to one row (single op). */
  const commitText = (card: AssetCard, col: ListColumn, text: string) => {
    if (text.trim() === cellText(col, card, detail(card)).trim()) return;
    const r = parseCell(col, text);
    if ('error' in r) return toast({ kind: 'error', title: r.error });
    if (r.value.kind === 'approval' && r.value.status === 'approved' && !canApproveCard(card)) return toast({ kind: 'error', title: 'You cannot approve this asset' });
    ops.commit(card, r.value);
  };
  /** Write one value to many rows: one bulk request (or the single op when only one row). */
  const writeMany = (targets: AssetCard[], value: CellValue) => {
    if (targets.length === 1) return ops.commit(targets[0]!, value);
    ops.fill(targets.map((t) => t.id), value);
  };
  const selectedCards = () => cards.filter((c): c is AssetCard => !isPlaceholder(c) && useSelection.getState().ids.has(c.id));

  const fill = () => {
    const card = assetCard(active.r);
    const col = cols[active.c]!;
    if (!card || !isEditable(col.kind)) return;
    const targets = selectedCards().filter((c) => c.id !== card.id);
    if (targets.length === 0) return toast({ kind: 'info', title: 'Select the rows to fill first', body: 'Space or Shift+↑/↓ selects rows; Ctrl+D then copies the active cell into them.' });
    const value = sourceValue(col, card, detail(card));
    if (!value) return toast({ kind: 'info', title: col.kind === 'field' ? 'The value is still loading' : 'Nothing to fill' });
    if (col.kind === 'title') return toast({ kind: 'info', title: 'Titles are edited one asset at a time' });
    ops.fill(targets.map((t) => t.id), value);
  };

  const onPaste = (e: React.ClipboardEvent) => {
    if (editing || (e.target as HTMLElement).closest('input,textarea,select')) return;
    const col = cols[active.c]!;
    const card = assetCard(active.r);
    if (!card || !isEditable(col.kind)) return;
    const text = e.clipboardData.getData('text/plain');
    if (!text) return;
    e.preventDefault();
    const lines = text.replace(/\r/g, '').split('\n');
    if (lines.length > 1 && lines[lines.length - 1] === '') lines.pop();
    // one line → every selected row (when the active row is one of them); several → one per row from the active one
    const groups = new Map<string, AssetCard[]>();
    if (lines.length === 1) {
      const sel = selectedCards();
      groups.set(lines[0]!, sel.some((c) => c.id === card.id) ? sel : [card]);
    } else {
      let r = active.r;
      for (const line of lines) {
        while (r < cards.length && !assetCard(r)) r++;
        const target = assetCard(r++);
        if (!target) break;
        groups.set(line, [...(groups.get(line) ?? []), target]);
      }
    }
    for (const [line, targets] of groups) {
      const editable = targets.filter(canEditCard);
      if (!editable.length) continue;
      if (col.kind === 'title') {
        for (const t of editable) commitText(t, col, line);
        continue;
      }
      const parsed = parseCell(col, line);
      if ('error' in parsed) {
        toast({ kind: 'error', title: parsed.error });
        continue;
      }
      writeMany(editable, parsed.value);
    }
  };

  const onCopy = (e: React.ClipboardEvent) => {
    if (editing || (e.target as HTMLElement).closest('input,textarea,select')) return;
    const card = assetCard(active.r);
    const col = cols[active.c]!;
    if (!card || !isEditable(col.kind)) return;
    e.preventDefault();
    e.clipboardData.setData('text/plain', cellText(col, card, detail(card)));
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (editing || (e.target as HTMLElement).closest('input,textarea,select,button')) return;
    const mod = e.metaKey || e.ctrlKey;
    const card = assetCard(active.r);
    const col = cols[active.c]!;
    const handled = () => {
      e.preventDefault();
      e.stopPropagation();
    };
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      handled();
      const next = active.r + (e.key === 'ArrowDown' ? 1 : -1);
      if (e.shiftKey) {
        if (card) rangeRow(card);
        const n = assetCard(Math.max(0, Math.min(cards.length - 1, next)));
        if (n) rangeRow(n);
      }
      moveTo(next, active.c);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      handled();
      moveTo(active.r, active.c + (e.key === 'ArrowRight' ? 1 : -1));
    } else if (e.key === 'Home' || e.key === 'End') {
      handled();
      moveTo(mod ? (e.key === 'Home' ? 0 : cards.length - 1) : active.r, e.key === 'Home' ? 0 : cols.length - 1);
    } else if (e.key === ' ' && card) {
      handled();
      toggleRow(card);
    } else if ((e.key === 'Enter' || e.key === 'F2') && card) {
      handled();
      if (isEditable(col.kind) && canEditCard(card)) setEditing(true);
      else if (e.key === 'Enter') onOpen(card);
    } else if (mod && (e.key === 'd' || e.key === 'D')) {
      handled();
      fill();
    } else if (mod && (e.key === 'a' || e.key === 'A')) {
      handled();
      useSelection.getState().selectAll();
    } else if (e.key === 'Escape' && useSelection.getState().ids.size) {
      handled();
      useSelection.getState().clear();
    } else if ((e.key === 'Delete' || e.key === 'Backspace') && card && canEditCard(card)) {
      if (col.kind === 'rights' || col.kind === 'field') {
        handled();
        if (cellText(col, card, detail(card))) ops.commit(card, col.kind === 'rights' ? { kind: 'rights', expires_at: null } : { kind: 'field', field: col.field!, value: null });
      } else if (col.kind === 'tags' && tagsOf(card).length) {
        handled();
        ops.removeTag(card, tagsOf(card).at(-1)!);
      }
    }
  };

  const onFocusCell = (e: React.FocusEvent) => {
    const cell = (e.target as HTMLElement).closest<HTMLElement>('[data-cell]');
    if (!cell) return;
    const [r, c] = cell.dataset.cell!.split(':').map(Number) as [number, number];
    if (r !== active.r || c !== active.c) {
      setActive({ r, c });
      setEditing(false);
    }
  };

  const nSelected = selected.size;
  return (
    <div data-list-view>
      <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-fg-subtle">
        <span className="hidden md:inline">↑↓←→ move · Enter edits · Space / Shift+↑↓ select · Ctrl+D fills the selection · paste writes into the selected rows</span>
        {nSelected > 0 && <span className="text-fg-muted">{plural(nSelected, 'row', 'rows')} selected</span>}
        {assetFields.length > 0 && <FieldColumnPicker fields={assetFields} shown={shownFields.map((f) => f.id)} onChange={setFieldIds} />}
      </div>
      <div className="overflow-x-auto">
        <table ref={tableRef} role="grid" aria-label="Assets" aria-rowcount={cards.length + 1} aria-multiselectable="true" className="w-full text-sm" onKeyDown={onKeyDown} onPaste={onPaste} onCopy={onCopy} onFocus={onFocusCell}>
          <thead className="text-left text-xs text-fg-subtle">
            <tr>
              {cols.map((c) => (
                <th key={c.key} scope="col" className={cx('py-1 pr-2 font-medium whitespace-nowrap', c.className)}>
                  {c.kind === 'select' || c.kind === 'thumb' ? <span className="sr-only">{c.label}</span> : c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cards.map((c, r) =>
              isPlaceholder(c) ? (
                <tr key={c.id} className="border-t border-line text-fg-muted">
                  <td />
                  <td className="py-1.5">{c.preview_url && <img src={c.preview_url} alt="" className="h-8 w-10 rounded object-cover" />}</td>
                  <td colSpan={cols.length - 2}>
                    {c.title} — {c.status} {c.status === 'uploading' ? `${Math.round(c.progress * 100)}%` : ''}
                  </td>
                </tr>
              ) : (
                <ListRow
                  key={c.id}
                  card={c}
                  r={r}
                  cols={cols}
                  activeCol={active.r === r ? active.c : -1}
                  editing={editing && active.r === r}
                  selected={selected.has(c.id)}
                  canEdit={canEditCard(c)}
                  canApprove={canApproveCard(c)}
                  needsDetail={shownFields.length > 0}
                  onToggle={toggleRow}
                  onOpen={onOpen}
                  onEdit={(col) => {
                    setActive({ r, c: col });
                    setEditing(true);
                  }}
                  onDone={(text, move) => {
                    setEditing(false);
                    if (text != null) commitText(c, cols[active.c]!, text);
                    if (move === 'down') moveTo(r + 1, active.c);
                    else if (move === 'right') moveTo(r, active.c + 1);
                    else setFocusTick((t) => t + 1);
                  }}
                  onRemoveTag={ops.removeTag}
                />
              ),
            )}
          </tbody>
        </table>
      </div>
      <div ref={sentinel} className="h-px" />
    </div>
  );
}

type RowProps = {
  card: AssetCard;
  r: number;
  cols: ListColumn[];
  /** index of the active cell when it is in this row, else -1 */
  activeCol: number;
  editing: boolean;
  selected: boolean;
  canEdit: boolean;
  canApprove: boolean;
  needsDetail: boolean;
  onToggle: (c: AssetCard) => void;
  onOpen: (c: AssetCard) => void;
  onEdit: (col: number) => void;
  onDone: (text: string | null, move: 'down' | 'right' | 'none') => void;
  onRemoveTag: (c: AssetCard, name: string) => void;
};

const ListRow = memo(function ListRow({ card, r, cols, activeCol, editing, selected, canEdit, canApprove, needsDetail, onToggle, onOpen, onEdit, onDone, onRemoveTag }: RowProps) {
  const ref = useRef<HTMLTableRowElement>(null);
  const asset = useRowDetail(ref, card.id, needsDetail);
  return (
    <tr ref={ref} aria-rowindex={r + 2} aria-selected={selected} className={cx('border-t border-line', selected ? 'bg-accent-soft/60' : 'hover:bg-bg-sunken')} data-row={card.id}>
      {cols.map((col, ci) => {
        const isActive = ci === activeCol;
        const editable = isEditable(col.kind) && canEdit;
        return (
          <td
            key={col.key}
            role="gridcell"
            data-cell={`${r}:${ci}`}
            data-col={col.key}
            tabIndex={isActive ? 0 : -1}
            aria-readonly={isEditable(col.kind) ? !editable : undefined}
            className={cx('py-1 pr-2 align-middle outline-none', col.className, isActive && 'ring-2 ring-accent ring-inset', col.kind === 'title' && 'font-medium')}
            onDoubleClick={() => (editable ? onEdit(ci) : onOpen(card))}
          >
            {isActive && editing && editable ? <CellEditor col={col} card={card} asset={asset} canApprove={canApprove} onDone={onDone} /> : <CellView col={col} card={card} asset={asset} selected={selected} canEdit={canEdit} needsDetail={needsDetail} onToggle={onToggle} onRemoveTag={onRemoveTag} />}
          </td>
        );
      })}
    </tr>
  );
});

function CellView({ col, card, asset, selected, canEdit, needsDetail, onToggle, onRemoveTag }: { col: ListColumn; card: AssetCard; asset: Asset | null; selected: boolean; canEdit: boolean; needsDetail: boolean; onToggle: (c: AssetCard) => void; onRemoveTag: (c: AssetCard, name: string) => void }) {
  switch (col.kind) {
    case 'select':
      return (
        <span role="checkbox" aria-checked={selected} aria-label={`Select ${card.title}`} onClick={() => onToggle(card)} className={cx('flex h-4 w-4 cursor-pointer items-center justify-center rounded border', selected ? 'border-accent bg-accent text-white' : 'border-line-strong')}>
          {selected && <span className="h-1.5 w-1.5 rounded-sm bg-white" />}
        </span>
      );
    case 'thumb':
      return card.thumbnails['256'] ? <img src={card.thumbnails['256']} alt="" className="h-8 w-10 rounded object-cover" style={{ background: card.dominant_colors?.[0]?.hex }} loading="lazy" /> : null;
    case 'title':
      return <span className="block max-w-[24rem] truncate" title={card.title}>{card.title}</span>;
    case 'type':
      return (
        <span className="text-fg-muted">
          {card.type}
          {card.duration_ms ? ` · ${msToClock(card.duration_ms)}` : ''}
        </span>
      );
    case 'approval':
      return card.approval_status !== 'none' ? <Badge tone={APPROVAL_TONE[card.approval_status].tone}>{APPROVAL_TONE[card.approval_status].label}</Badge> : <span className="text-fg-subtle">—</span>;
    case 'tags':
      return (
        <span className="flex flex-wrap gap-1">
          {tagsOf(card).map((n) => (
            <span key={n} className="inline-flex items-center gap-0.5 rounded-full border border-line bg-bg-sunken px-1.5 text-xs">
              {n}
              {canEdit && (
                <button type="button" tabIndex={-1} aria-label={`Remove tag ${n}`} className="text-fg-subtle hover:text-danger" onClick={() => onRemoveTag(card, n)}>
                  <X size={10} />
                </button>
              )}
            </span>
          ))}
          {tagsOf(card).length === 0 && <span className="text-fg-subtle">—</span>}
        </span>
      );
    case 'rights': {
      const s = card.rights_status ?? 'none';
      if (!card.rights_expires_at) return <span className="text-fg-subtle">—</span>;
      return (
        <span className="flex items-center gap-1 whitespace-nowrap" data-rights-cell={s}>
          {cellText(col, card)}
          {(s === 'expiring' || s === 'expired') && <Badge tone={RIGHTS_STATUS_TONE[s]}>{RIGHTS_STATUS_LABEL[s]}</Badge>}
        </span>
      );
    }
    case 'field':
      if (needsDetail && !asset) return <span className="text-fg-subtle">…</span>;
      return <span className="block max-w-[14rem] truncate">{cellText(col, card, asset) || <span className="text-fg-subtle">—</span>}</span>;
    case 'size':
      return <span className="text-fg-muted">{formatBytes(card.size_bytes)}</span>;
    case 'added':
      return <span className="text-fg-muted">{formatDateTime(card.added_at ?? card.created_at)}</span>;
  }
}

/** Inline editor; Enter saves and moves down, Tab saves and moves right, Escape cancels, blur saves. */
function CellEditor({ col, card, asset, canApprove, onDone }: { col: ListColumn; card: AssetCard; asset: Asset | null; canApprove: boolean; onDone: (text: string | null, move: 'down' | 'right' | 'none') => void }) {
  const initial = col.kind === 'tags' ? '' : cellText(col, card, asset);
  const [text, setText] = useState(initial);
  const done = useRef(false);
  const finish = (value: string | null, move: 'down' | 'right' | 'none') => {
    if (done.current) return;
    done.current = true;
    onDone(value, move);
  };
  const keys = (e: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement>) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      e.preventDefault();
      finish((e.target as HTMLInputElement).value, 'down');
    } else if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      finish((e.target as HTMLInputElement).value, 'right');
    } else if (e.key === 'Escape') {
      e.preventDefault();
      finish(null, 'none');
    }
  };
  const label = `${col.label} for ${card.title}`;
  const options =
    col.kind === 'approval'
      ? APPROVAL_STATUSES.filter((s) => s !== 'approved' || canApprove || card.approval_status === 'approved').map((s) => ({ value: APPROVAL_TONE[s].label, label: APPROVAL_TONE[s].label }))
      : col.kind === 'field' && col.field!.type === 'select'
        ? [{ value: '', label: '—' }, ...(col.field!.options ?? []).map((o) => ({ value: o.label, label: o.label }))]
        : null;
  if (options) {
    return (
      <Select autoFocus aria-label={label} value={text} className="h-7 text-xs" onChange={(e) => finish(e.target.value, 'none')} onKeyDown={keys} onBlur={() => finish(null, 'none')}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </Select>
    );
  }
  const type = col.kind === 'rights' || (col.kind === 'field' && col.field!.type === 'date') ? 'date' : col.kind === 'field' && col.field!.type === 'number' ? 'number' : 'text';
  return (
    <Input
      autoFocus
      aria-label={label}
      type={type}
      value={text}
      placeholder={col.kind === 'tags' ? 'Add tags, comma separated' : col.kind === 'field' && col.field!.type === 'multi_select' ? 'Options, comma separated' : undefined}
      className="h-7 min-w-[8rem] text-xs"
      onChange={(e) => setText(e.target.value)}
      onKeyDown={keys}
      onBlur={(e) => finish(e.target.value === initial ? null : e.target.value, 'none')}
    />
  );
}

/** The writes behind the cells: single ops for one row, one bulk request for a fill / paste. */
function useListOps(wsId: string) {
  const qc = useQueryClient();
  const update = useUpdateAsset();
  const approval = useSetApproval();
  const bulk = useBulkAction(wsId);
  const rights = useBulkSetAssetRights(wsId);
  const tags = useTags(wsId || undefined);
  const fail = (title: string) => (e: unknown) => toast({ kind: 'error', title: isApiError(e) ? e.message : title });
  const patchTags = (c: AssetCard, names: string[]) => patchCardEverywhere(qc, { id: c.id, tag_names: [...new Set(names)].sort() } as AssetCard);
  const addTags = useMutation({
    mutationFn: ({ card, names }: { card: AssetCard; names: string[] }) => req(api.POST('/assets/{assetId}/tags', { params: { path: { assetId: card.id } }, body: { names } })),
    onSuccess: (_r, v) => {
      patchTags(v.card, [...tagsOf(v.card), ...v.names]);
      void qc.invalidateQueries({ queryKey: keys.asset(v.card.id) });
      void qc.invalidateQueries({ queryKey: keys.tags(wsId) });
    },
    onError: fail('Could not add tags'),
  });
  const removeTag = useMutation({
    mutationFn: ({ card, tagId }: { card: AssetCard; name: string; tagId: string }) => req(api.DELETE('/assets/{assetId}/tags/{tagId}', { params: { path: { assetId: card.id, tagId } } })),
    onSuccess: (_r, v) => {
      patchTags(v.card, tagsOf(v.card).filter((n) => n !== v.name));
      void qc.invalidateQueries({ queryKey: keys.asset(v.card.id) });
    },
    onError: fail('Could not remove the tag'),
  });
  const setField = useMutation({
    mutationFn: ({ card, fieldId, value }: { card: AssetCard; fieldId: string; value: object | null }) =>
      value ? req(api.PUT('/assets/{assetId}/custom-fields/{fieldId}', { params: { path: { assetId: card.id, fieldId } }, body: value })) : req(api.DELETE('/assets/{assetId}/custom-fields/{fieldId}', { params: { path: { assetId: card.id, fieldId } } })),
    onSettled: (_r, _e, v) => void qc.invalidateQueries({ queryKey: keys.asset(v.card.id) }),
    onError: fail('Could not save the field'),
  });
  const report = (r: BulkRightsResult) => {
    if (!r.failed.length) return toast({ kind: 'success', title: `Updated ${plural(r.succeeded.length, 'asset', 'assets')}` });
    const why = [...new Set(r.failed.map((f) => (f.code === 'permission_denied' ? 'no permission' : (f.message ?? f.code))))].join(', ');
    toast({ kind: 'info', title: `Updated ${plural(r.succeeded.length, 'asset', 'assets')}`, body: `${plural(r.failed.length, 'asset was', 'assets were')} skipped (${why})` });
  };
  return {
    commit(card: AssetCard, v: CellValue) {
      switch (v.kind) {
        case 'title':
          return update.mutate({ assetId: card.id, patch: { title: v.title } }, { onError: fail('Could not rename') });
        case 'approval':
          return approval.mutate({ assetId: card.id, status: v.status }, { onError: fail('Could not set approval') });
        case 'tags': {
          const fresh = v.names.filter((n) => !tagsOf(card).includes(n));
          return fresh.length ? addTags.mutate({ card, names: fresh }) : undefined;
        }
        case 'rights':
          return rights.mutate({ asset_ids: [card.id], rights: { expires_at: v.expires_at }, merge: true }, { onError: fail('Could not set the rights expiry') });
        case 'field':
          return setField.mutate({ card, fieldId: v.field.id, value: v.value });
      }
    },
    fill(ids: string[], v: CellValue) {
      const plan = bulkPlan(v);
      if (plan.op === 'none') return toast({ kind: 'info', title: plan.reason });
      if (plan.op === 'rights') return rights.mutate({ asset_ids: ids, rights: plan.rights, merge: true }, { onSuccess: report, onError: fail('Could not set the rights expiry') });
      bulk.mutate({ asset_ids: ids, action: plan.action, params: plan.params }, { onSuccess: (r) => report(r as BulkRightsResult), onError: fail('Bulk edit failed') });
    },
    removeTag(card: AssetCard, name: string) {
      const tag = (tags.data ?? []).find((t) => t.name.toLowerCase() === name.toLowerCase());
      if (!tag) return toast({ kind: 'error', title: `Tag “${name}” not found` });
      removeTag.mutate({ card, name, tagId: tag.id });
    },
  };
}

/** Field columns shown in the list (device-local); null = the first few asset fields. */
function useFieldColumns(): [string[] | null, (ids: string[]) => void] {
  const [ids, setIds] = useState<string[] | null>(() => {
    try {
      const raw = localStorage.getItem(FIELD_COLUMNS_KEY);
      return raw ? (JSON.parse(raw) as string[]) : null;
    } catch {
      return null;
    }
  });
  const set = (next: string[]) => {
    setIds(next);
    try {
      localStorage.setItem(FIELD_COLUMNS_KEY, JSON.stringify(next));
    } catch {
      /* private mode: keep it for this session */
    }
  };
  return [ids, set];
}

function FieldColumnPicker({ fields, shown, onChange }: { fields: Array<{ id: string; name: string }>; shown: string[]; onChange: (ids: string[]) => void }) {
  return (
    <details className="relative ml-auto">
      <summary className="flex cursor-pointer list-none items-center gap-1 rounded px-1.5 py-0.5 hover:bg-bg-sunken" aria-label="Field columns">
        <Columns3 size={12} /> Fields
      </summary>
      <div className="absolute right-0 z-20 mt-1 w-56 space-y-1 rounded-md border border-line bg-bg-elev p-2 shadow-lg">
        {fields.map((f) => (
          <label key={f.id} className="flex items-center gap-2 text-xs text-fg">
            <input type="checkbox" checked={shown.includes(f.id)} onChange={(e) => onChange(e.target.checked ? [...shown, f.id] : shown.filter((x) => x !== f.id))} /> {f.name}
          </label>
        ))}
      </div>
    </details>
  );
}
